import React, { memo, useCallback, useEffect, useState } from 'react'
import { Image, ScrollView, StyleSheet, View, Pressable, type ImageSourcePropType } from 'react-native'
import { useRouter } from 'expo-router'
import { useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { TOURNAMENT_STATE } from '@ppwr/convex/shared/tournamentConstants'
import { DS } from '@/design-system'
import { useStore } from '@/models'
import { formatShortDateTime, formatTimeUntilStart } from '../../utils/dateFormatter'
import { openLobby } from './mttNavigation'
import pattern1 from '../play-screen/Sections/YourGames/assets/pattern1.png'
import pattern2 from '../play-screen/Sections/YourGames/assets/pattern2.png'
import pattern3 from '../play-screen/Sections/YourGames/assets/pattern3.png'
import pattern4 from '../play-screen/Sections/YourGames/assets/pattern4.png'

const patterns = [pattern1, pattern2, pattern3, pattern4]

function formatTournamentState(state: string): string {
  switch (state) {
    case TOURNAMENT_STATE.REGISTRATION:
      return 'Registration Open'
    case TOURNAMENT_STATE.ACTIVE:
      return 'In Progress'
    case TOURNAMENT_STATE.FINISHED:
      return 'Finished'
    default:
      return state
  }
}

function formatStartTime(ts: number, now: number): string {
  const diffMs = ts - now
  if (diffMs < 0) return formatShortDateTime(new Date(ts))
  const { label, isDate } = formatTimeUntilStart(diffMs, new Date(ts))
  return isDate ? `Starts ${label}` : `Starts in ${label}`
}

function stateColor(state: string): string {
  switch (state) {
    case TOURNAMENT_STATE.REGISTRATION:
      return DS.palette['Brand/Blue']
    case TOURNAMENT_STATE.ACTIVE:
      return DS.palette['Utility/Success']
    case TOURNAMENT_STATE.FINISHED:
      return DS.palette['Brand/Dark Grey']
    default:
      return DS.palette['Brand/Dark Grey']
  }
}

/**
 * Leaf component that owns the 30-second ticker for relative-start labels.
 * Pulled out of the parent so only this `<DS.Text>` re-renders on tick —
 * the previous shape lifted `now` into `MyTournaments`, which re-rendered
 * every card (and the surrounding scroll view) every 30 seconds.
 */
const RelativeStartTime = memo(function RelativeStartTime({ scheduledStartTime }: { scheduledStartTime: number }) {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(id)
  }, [])
  return (
    <DS.Text variant="Body Small/Regular" style={{ color: DS.palette['Brand/Dark Grey'] }}>
      {formatStartTime(scheduledStartTime, now)}
    </DS.Text>
  )
})

type TournamentSummary = {
  _id: string
  name: string
  state: string
  scheduledStartTime: number
  registrationCount: number
  code?: string
  winnerName?: string
}

interface CardProps {
  tournament: TournamentSummary
  patternImage: ImageSourcePropType
  onPress: (id: string) => void
}

const TournamentCard = memo(function TournamentCard({ tournament: t, patternImage, onPress }: CardProps) {
  const handlePress = useCallback(() => onPress(t._id), [onPress, t._id])
  const isUpcoming = t.state === 'registration'
  const isHistorical = t.state === 'active' || t.state === 'finished'
  return (
    <Pressable onPress={handlePress}>
      <DS.Frame bg="Global/White" style={styles.card}>
        <DS.Frame style={styles.cardBody}>
          <DS.Frame gap="8">
            <DS.Text variant="Body Small/Regular" style={{ color: stateColor(t.state) }}>
              {formatTournamentState(t.state)}
            </DS.Text>
            <DS.Text variant="Heading 4/Medium" numberOfLines={1}>
              {t.name}
            </DS.Text>
            <DS.Frame gap="6" row>
              <DS.Icon name="Person" color="Brand/Orange" size={16} />
              <DS.Text variant="Body Small/Regular">
                {t.registrationCount} player{t.registrationCount !== 1 ? 's' : ''}
              </DS.Text>
            </DS.Frame>
            {isHistorical ? (
              <DS.Text variant="Body Small/Regular" style={{ color: DS.palette['Brand/Dark Grey'] }}>
                {formatShortDateTime(new Date(t.scheduledStartTime))}
              </DS.Text>
            ) : (
              <RelativeStartTime scheduledStartTime={t.scheduledStartTime} />
            )}
            {isUpcoming && (
              <DS.Frame gap="4" row style={{ alignItems: 'center' }}>
                <DS.Text variant="Mini/Regular" style={{ color: DS.palette['Brand/Dark Grey'] }}>Code:</DS.Text>
                <DS.Text variant="Mini/Bold" style={{ letterSpacing: 1 }}>
                  {t.code}
                </DS.Text>
              </DS.Frame>
            )}
            {t.state === 'finished' && t.winnerName && (
              <DS.Frame gap="4" row style={{ alignItems: 'center' }}>
                <DS.Icon name="Crown" color="Brand/Orange" size={14} />
                <DS.Text variant="Mini/Regular" numberOfLines={1}>
                  {t.winnerName}
                </DS.Text>
              </DS.Frame>
            )}
          </DS.Frame>
          <DS.Button variant="Medium/Secondary" type="On Dark" onPress={handlePress}>
            View
          </DS.Button>
        </DS.Frame>
        <Image source={patternImage} style={styles.pattern} />
      </DS.Frame>
    </Pressable>
  )
})

export function MyTournaments() {
  const router = useRouter()
  const { user } = useStore().session
  const userId = user?.auth0Id || ''

  // Server uses the auth subject; only gate the subscription on a hydrated user
  // so we don't fire an `Unauthorized` error while auth is still hydrating.
  const tournaments = useQuery(
    api.tournaments.queries.getTournamentsForUser,
    userId ? {} : 'skip'
  )

  const navigateToTournament = useCallback((tournamentId: string) => {
    openLobby(router, tournamentId)
  }, [router])

  if (!tournaments?.length) return null

  return (
    <View style={styles.webWrapper}>
      <DS.Frame gap="12" style={{ marginHorizontal: -DS.spacing[24] }}>
        <DS.Text style={{ paddingHorizontal: 24 }} variant="Heading 3/Medium">
          Your Tournaments {tournaments.length > 2 ? `(${tournaments.length})` : ''}
        </DS.Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 24 }}>
          <DS.Frame row gap="8">
            {tournaments.map((t, i) => (
              <TournamentCard
                key={t._id}
                tournament={t as TournamentSummary}
                patternImage={patterns[i % patterns.length]}
                onPress={navigateToTournament}
              />
            ))}
          </DS.Frame>
        </ScrollView>
      </DS.Frame>
    </View>
  )
}

const styles = StyleSheet.create({
  webWrapper: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center' as const
  },
  card: {
    borderRadius: DS.spacing[16],
    height: 227,
    width: 204
  },
  cardBody: {
    borderTopRightRadius: DS.spacing[16],
    borderTopLeftRadius: DS.spacing[16],
    padding: DS.spacing[16],
    justifyContent: 'space-between',
    height: 211
  },
  pattern: {
    width: '100%',
    height: 16,
    borderBottomLeftRadius: DS.spacing[16],
    borderBottomRightRadius: DS.spacing[16]
  }
})
